import React, { useEffect, useState } from "react";
import { Table, Spin } from "antd";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import aiConsoleService from "services/aiconsoleservice";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(4)}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

const StatCard = ({ label, value }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-800 dark:text-white">{value}</div>
  </div>
);

const Card = ({ title, children, className = "" }) => (
  <div className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 ${className}`}>
    <div className="text-sm font-medium mb-3 text-slate-700 dark:text-gray-200">{title}</div>
    {children}
  </div>
);

// When `agentData` is passed (per-agent view), render it directly without fetching.
// Otherwise self-fetch the global summary (the standalone Observability page).
const Observability = ({ agentData }) => {
  const scoped = agentData !== undefined;
  const [data, setData] = useState(scoped ? agentData : null);
  const [loading, setLoading] = useState(!scoped);

  useEffect(() => {
    if (scoped) {
      setData(agentData ?? null);
      setLoading(false);
      return;
    }
    let active = true;
    aiConsoleService
      .getObservability()
      .then((res) => {
        if (active) {
          setData(res);
          setLoading(false);
        }
      })
      .catch(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [scoped, agentData]);

  if (loading) return <div className="p-8 flex justify-center"><Spin /></div>;
  if (!data) return <div className="p-8 text-center text-gray-400">No data yet.</div>;

  // Empty state: an agent (e.g. a coming-soon one) that has produced no messages.
  if (((data.totals || {}).messages || 0) === 0) {
    return (
      <div className="p-10 text-center text-gray-400 dark:text-gray-500" data-testid="observability-empty">
        No activity yet for this agent.
      </div>
    );
  }

  const t = data.totals || {};
  const recentCols = [
    { title: "Scope", dataIndex: "aim_type", key: "scope" },
    { title: "Model", dataIndex: "aim_model", key: "model", render: (m) => <span className="text-xs">{m}</span> },
    { title: "In", dataIndex: "aim_input_tokens", key: "in", render: fmtNum },
    { title: "Out", dataIndex: "aim_output_tokens", key: "out", render: fmtNum },
    { title: "Latency", dataIndex: "aim_latency_ms", key: "lat", render: (v) => `${fmtNum(v)} ms` },
    { title: "Cost", dataIndex: "aim_cost", key: "cost", render: fmtUsd },
    { title: "When", dataIndex: "created_at", key: "when" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Cost" value={fmtUsd(t.cost)} />
        <StatCard label="Messages" value={fmtNum(t.messages)} />
        <StatCard label="Tokens" value={fmtNum((t.input_tokens || 0) + (t.output_tokens || 0))} />
        <StatCard label="Avg Latency" value={`${fmtNum(t.avg_latency_ms)} ms`} />
      </div>

      <Card title="Cost over time">
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <LineChart data={(data.cost_over_time || []).map((d) => ({ date: d.date, cost: Number(d.cost) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtUsd(v)} />
              <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="By scope">
          <Table
            size="small"
            pagination={false}
            rowKey="scope"
            dataSource={data.by_scope || []}
            columns={[
              { title: "Scope", dataIndex: "scope" },
              { title: "Msgs", dataIndex: "messages" },
              { title: "Cost", dataIndex: "cost", render: fmtUsd },
            ]}
          />
        </Card>
        <Card title="By model">
          <Table
            size="small"
            pagination={false}
            rowKey="model"
            dataSource={data.by_model || []}
            columns={[
              { title: "Model", dataIndex: "model", render: (m) => <span className="text-xs">{m}</span> },
              { title: "Msgs", dataIndex: "messages" },
              { title: "Cost", dataIndex: "cost", render: fmtUsd },
            ]}
          />
        </Card>
      </div>

      <Card title="Recent conversations" className="overflow-x-auto">
        <Table
          size="small"
          rowKey="aim_id"
          dataSource={data.recent || []}
          columns={recentCols}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default Observability;
